import { TableBody, TableRow } from '@mui/material';
import clsx from 'clsx';
import { forwardRef, useCallback, useRef, useState } from 'react';

import { ColumnConfig, useRowVisibilityContext } from '../contexts';
import SubRow from '../SubRow';
import SubRowCell from '../SubRowCell';
import { Cell } from '../TableRows/Cell';
import { BaseRow } from '../types';
import { VirtualRowProps } from './types';

const VirtualRow = forwardRef<HTMLTableSectionElement, VirtualRowProps>(
  (
    {
      index,
      data: {
        classes,
        expandOnClick,
        getRow,
        isExpanded,
        isSelected,
        onClick,
        onContextClick,
        orderedColumns,
        setClassName,
        SubRowComponent,
        toggleExpand,
      },
    },
    rowRef
  ) => {
    const row = getRow(index);
    const rowClassName = setClassName?.(row);
    const selected = isSelected(row.id);
    const expanded = isExpanded(row.id);

    const dragRowRef = useRef<HTMLTableRowElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const rowVisibilityContext = useRowVisibilityContext();

    const renderRowColumn = useCallback(
      (column: ColumnConfig, row: BaseRow) => {
        const { bodyClassName, className, name, render, getValue, wrap, minWidth } = column;
        // these could be undefined if controls have initialized first
        if (!getValue && !render && !wrap) {
          return null;
        }
        const value = getValue?.current(row, false);

        return (
          <Cell
            minWidth={minWidth}
            isExpanded={!!(SubRowComponent && expanded)}
            bodyClassName={bodyClassName}
            className={className}
            name={name}
            render={render}
            value={value}
            wrap={wrap}
            row={row}
            key={`row_${row.id}_${name}`}
            rowRef={dragRowRef}
          />
        );
      },
      [SubRowComponent, expanded]
    );

    return (
      <TableBody ref={rowRef} key={`row_${row.id}`} data-index={index}>
        <TableRow
          key={`row_${row.id}_tableRow`}
          hover
          selected={selected}
          className={clsx({
            // render cursor pointer if the row has a left or right click handler
            [classes.cursorPointer]: !!(onClick || onContextClick || expandOnClick),
            row_selected: selected,
            [rowClassName!]: !!rowClassName,
            [classes.hiddenRow]: rowVisibilityContext?.hiddenRowIds.includes(row.id),
          })}
          data-id={row.id}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => setIsDragging(false)}
          onClick={(e) => {
            onClick?.(e, row);
            expandOnClick && toggleExpand(e);
          }}
          onContextMenu={(e) => onContextClick?.(e, row)}
          ref={dragRowRef}
          id={clsx({ dragRow: isDragging })}
        >
          <SubRowCell
            row={row}
            classes={classes}
            onClick={toggleExpand}
            enabled={!!SubRowComponent}
            expanded={isExpanded(row.id)}
          />
          {orderedColumns.map((column) => renderRowColumn(column, row))}
        </TableRow>
        <SubRow
          Component={SubRowComponent}
          classes={classes}
          enabled={!!SubRowComponent}
          expanded={isExpanded(row.id)}
          row={row}
        />
      </TableBody>
    );
  }
);

export default VirtualRow;
